import React, { useState } from 'react';
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    Paper,
    Tooltip,
    Alert
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import InfoIcon from "@mui/icons-material/Info";
import GetAppIcon from '@mui/icons-material/GetApp';
import { useData } from "@pure-ptr/react";
import { bulkSearchFromNPIRegistry, createBulkProviders } from "../../store/api/contractProviders";
import {  Loadable } from "../../common";
import { infoToastify, errorToastify, successToastify } from "../../util/toast";

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: "absolute",
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500]
    },
    tableContainer: {
        marginTop: theme.spacing(2),
        maxHeight: 400,
    },
    errorText: {
        color: theme.palette.error.main,
        fontStyle: 'italic',
        fontSize: '0.85rem'
    }
}));

export default function ProvidersBulkAddDialog({
    open,
    handleClose,
}) {
    const classes = useStyles();
    const [npiInput, setNpiInput] = useState("");
    const [providers, setProviders] = useState([]);
    const [existingNpis, setExistingNpis] = useState([]);
    const [npiInputError, setNpiInputError] = useState("");

    const [searchPayload, setSearchPayload] = useState(null);
    const [savePayload, setSavePayload] = useState(null);

    const {
        isPending: searchPending,
    } = useData(async () => {
        if (!searchPayload) return null;
        const payload = searchPayload;
        setSearchPayload(null);
        try {
            const res = await bulkSearchFromNPIRegistry(payload);
            if (res) {
                setExistingNpis(res.existingNpis || []);
                const results = res.results || [];
                const mappedProviders = results.map(item => ({
                    ...item,
                    selected: false,
                }));
                setProviders(mappedProviders);
            }
            return res;
        } catch (error) {
            errorToastify("Failed to fetch NPIs from registry.");
        }
    }, [searchPayload]);

    const {
        isPending: savePending,
    } = useData(async () => {
        if (!savePayload) return null;
        const payload = savePayload;
        setSavePayload(null);
        try {
            const res = await createBulkProviders(payload);
            const selectedProvidersCount = providers.filter(p => p.selected && !p.isNotFound).length;
            infoToastify(`${selectedProvidersCount} provider(s) added, ${existingNpis.length} skipped.`);
            setProviders([]);
            setExistingNpis([]);
            setNpiInput("");
            handleClose(true);
            return res;
        } catch (error) {
            errorToastify("Failed to save bulk providers.");
        }
    }, [savePayload, providers]);




    const handleSearch = () => {
        if (!npiInput.trim()) return;

        const rawNpis = npiInput.split(',').map(n => n.replace(/\s+/g, ''));
        const validNpis = rawNpis.filter(n => n.length > 0);

        // Remove duplicates
        const uniqueNpis = [...new Set(validNpis)];

        if (uniqueNpis.length === 0) {
            setNpiInputError("Please enter valid NPIs.");
            return;
        }

        const invalidNpis = uniqueNpis.filter(n => !/^\d{10}$/.test(n));
        if (invalidNpis.length > 0) {
            setNpiInputError(`Invalid NPI(s) found: ${invalidNpis.slice(0, 5).join(', ')}${invalidNpis.length > 5 ? '...' : ''}. Each NPI must be exactly 10 digits.`);
            return;
        }

        if (uniqueNpis.length > 500) {
            setNpiInputError("Maximum 500 NPIs allowed.");
            return;
        }

        setNpiInputError("");
        setSearchPayload({ Npis: uniqueNpis.join(',') });
    };

    const handleSave = () => {
        const selectedProviders = providers.filter(p => p.selected && !p.isNotFound);
        if (selectedProviders.length === 0) {
            errorToastify("Please select at least one valid provider to save.");
            return;
        }

        // Validate
        for (const p of selectedProviders) {
            if (!p.providerName || !p.providerType) {
                errorToastify(`Provider Name and Provider Type are required for NPI ${p.npi}`);
                return;
            }
        }

        setSavePayload({
            providers: selectedProviders.map(p => ({
                npi: p.npi,
                providerName: p.providerName,
                providerType: p.providerType,
                taxonomyCode: p.taxonomyCode || '',
                specialty: p.specialty || '',
                status: p.status || 'Active',
                state: p.state || '',
                role: p.role || '',
                degree: p.degree || '',
            }))
        });
    };

    const handleSelect = (index) => {
        const newProviders = [...providers];
        if (!newProviders[index].isNotFound) {
            newProviders[index].selected = !newProviders[index].selected;
            setProviders(newProviders);
        }
    };

    const handleSelectAll = (event) => {
        const isChecked = event.target.checked;
        const newProviders = providers.map(p => {
            if (p.isNotFound) return p;
            return { ...p, selected: isChecked };
        });
        setProviders(newProviders);
    };

    const handleCloseDialog = () => {
        setProviders([]);
        setExistingNpis([]);
        setNpiInput("");
        handleClose();
        setNpiInputError("")
    }

const handleExportUnfound = () => {
        const unfound = providers.filter(p => p.isNotFound).map(p => p.npi);
        if (unfound.length === 0) {
            infoToastify("No unfound NPIs to export.");
            return;
        }
        const csvContent = "data:text/csv;charset=utf-8," + ["Unfound NPIs", ...unfound].join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "Unfound_NPIs.csv");
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const allSelectableSelected = providers.filter(p => !p.isNotFound).every(p => p.selected) && providers.filter(p => !p.isNotFound).length > 0;
    const someSelectableSelected = providers.filter(p => !p.isNotFound).some(p => p.selected) && !allSelectableSelected;

    return (
        <Dialog
            maxWidth="lg"
            fullWidth
            open={open}
            onClose={handleCloseDialog}
            aria-labelledby="bulk-add-dialog-title"
        >
            <DialogTitle id="bulk-add-dialog-title">
                <Grid container>
                    <Grid item xs={11}>
                        <Typography variant="h6">
                            Add Bulk Providers
                        </Typography>
                    </Grid>
                    <Grid item xs={1}>
                        <IconButton
                            aria-label="close"
                            className={classes.closeButton}
                            onClick={handleCloseDialog}
                            size="large">
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                </Grid>
            </DialogTitle>
            <Loadable loading={searchPending !== null || savePending !== null}>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            label="NPIs *"
                            variant="standard"
                            fullWidth
                            placeholder="Example: 1234567890, 9876543210, 1122334455"
                            value={npiInput}
                            onChange={(e) => {
                                setNpiInputError("");
                                setNpiInput(e.target.value);
                            }}
                            error={!!npiInputError}
                            helperText={npiInputError}
                            disabled={searchPending !== null || savePending !== null}
                            InputProps={{
                                endAdornment: (

                                    <IconButton onClick={handleSearch} size="small" disabled={searchPending !== null || savePending !== null}>
                                        <Tooltip title={"Search NPIs"} arrow placement="right">
                                            <SearchIcon fontSize="small" />
                                        </Tooltip>
                                    </IconButton>
                                )
                            }}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" && !event.shiftKey) {
                                    event.preventDefault();
                                    handleSearch();
                                }
                            }}
                        />
                    </Grid>

                    {existingNpis.length > 0 && (
                        <Grid item xs={12} display="flex" alignItems="center">
                            <Tooltip title={existingNpis.join(', ')} arrow placement="right">
                                <Alert severity="info" icon={<InfoIcon />} sx={{ py: 0, width: 'fit-content' }}>
                                    {existingNpis.length} NPI(s) skipped (already exist in the Provider table).
                                </Alert>
                            </Tooltip>
                        </Grid>
                    )}

                    {providers.some(p => p.isNotFound) && (
                            <Tooltip title="Export Unfound NPIs">
                                <IconButton
                                    aria-label="Export"
                                    size='small'
                                    onClick={handleExportUnfound}
                                    sx={{ml:'auto',mt:existingNpis.length >0 ?0:"20px"}}
                                >
                                    <GetAppIcon />
                                </IconButton>
                            </Tooltip>
                        )}

                    {(providers.length > 0 || searchPending !== null) && (
                        <Grid item xs={12}>
                        <TableContainer component={Paper} className={classes.tableContainer}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                color="primary"
                                                indeterminate={someSelectableSelected}
                                                checked={allSelectableSelected}
                                                onChange={handleSelectAll}
                                                disabled={searchPending !== null || savePending !== null || providers.filter(p => !p.isNotFound).length === 0}
                                            />
                                        </TableCell>
                                        <TableCell>NPI</TableCell>
                                        <TableCell>Provider Name</TableCell>
                                        <TableCell>Role</TableCell>
                                        <TableCell>Provider Type</TableCell>
                                        <TableCell>Degree</TableCell>
                                        <TableCell>Specialty</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>State</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {providers.map((row, index) => {
                                        if (row.isNotFound) {
                                            return (
                                                <TableRow key={index} hover>
                                                    <TableCell padding="checkbox">
                                                        <Checkbox disabled />
                                                    </TableCell>
                                                    <TableCell>{row.npi}</TableCell>
                                                    <TableCell colSpan={7}>
                                                        <span className={classes.errorText}>No record found for this NPI</span>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        }

                                        const isMissingData = row.isRequiredDataMissing;

                                        return (
                                            <TableRow key={index} hover selected={row.selected}>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        color="primary"
                                                        checked={row.selected}
                                                        onChange={() => handleSelect(index)}
                                                    />
                                                </TableCell>
                                                <TableCell>{row.npi}</TableCell>
                                                <TableCell>
                                                    {row.providerName}
                                                    {isMissingData && !row.providerName && <div className={classes.errorText}>Missing Provider Name</div>}
                                                </TableCell>
                                                <TableCell>
                                                    {row.role}
                                                </TableCell>
                                                <TableCell>
                                                    {row.providerType}
                                                    {isMissingData && !row.providerType && <div className={classes.errorText}>Missing Provider Type</div>}
                                                </TableCell>
                                                <TableCell>{row.degree}</TableCell>
                                                <TableCell>{row.specialty}</TableCell>
                                                <TableCell>{row.status}</TableCell>
                                                <TableCell>{row.state}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        </Grid>
                    )}
                </Grid>
                <Grid container className="justify-content-end pt-2 mt-3">
                    <Button type="button" onClick={handleCloseDialog} variant="contained" className="mr-3" disabled={searchPending !== null || savePending !== null}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="primary" onClick={providers?.length==0 ? handleSearch: handleSave} disabled={npiInput?.length==0 || searchPending !== null || savePending !== null || (providers?.length>0 && providers.filter(p => p.selected).length === 0)}>
                     {providers?.length === 0 ? 'Search':'Save' }   
                    </Button>
                </Grid>
            </DialogContent>
            </Loadable>
        </Dialog>
    );
}
